import { createReducer, on } from "@ngrx/store";
import { myWorkflowsRequestInitialState } from "../../initial-states/workflows/my-workflows-request.state";
import { addMyWorkflowRequest, loadMyWorkflowsRequest, loadMyWorkflowsRequestFailed, loadMyWorkflowsRequestSuccess, updateMyRequestStageApprove, upsertMyWorkflowExecutionStatus, upsertMyWorkflowRequest } from "../../actions/workflows/my-workflows-request.action";
import { IWorkflowRequest, IWorkflowRequestApprovalStage, parseFrontEndObjectProperties, WorkflowExecutionStatus, WorkflowRequestApprover, WorkflowRequestStatus } from "src/app/models/workflow-request.model";

export const MyWorkflowsRequestReducer = createReducer(myWorkflowsRequestInitialState,
    // The reducer runs when a dispatch 'loadMyWorkflowsRequest' was triggered
	// The only thing we need to do here is set loading to true
	on(loadMyWorkflowsRequest,
		(state) => {
			return { ...state, loading: true };
		}
	),

	// The reducer runs when a dispatch 'loadMyWorkflowsRequestSuccess' was triggered
	// Here we need to set the loading to false, set the value of 'loadedOnce' and add the value of the workflows for the specific type
	on(loadMyWorkflowsRequestSuccess,
		(state, { workflowsRequest }) => {
			return { ...state, loading: false, loadedOnce: true, value: workflowsRequest };
		}
	),

	// The reducer runs when a dispatch 'loadMyWorkflowsRequestFailed' was triggered
	// Here we need to set the state to the initial one, set the value of 'loadedOnce' and add the value of the error action result into the store
	on(loadMyWorkflowsRequestFailed,
		(state, { error }) => {
			return { ...state, loading: false, loadedOnce: false, error: error };
		}
	),

	// The reducer runs when a dispatch 'addMyWorkflowRequest' was triggered
	// Here we need to add a new workflow to the store
	on(addMyWorkflowRequest,
		(state, { workflow, setLoaded }) => {
			return { ...state, value: [...state.value, workflow], loadedOnce: setLoaded ? true : state.loadedOnce, loading: false};
		}
	),
	
	// The reducer runs when a dispatch 'upsertMyWorkflowRequest' was triggered
	// Here we need to update an existing workflow in the store
	on(upsertMyWorkflowRequest,
		(state, { workflow }) => {
			return { ...state, value: updateWorkflowByReference(state.value, workflow) };
		}
	),

	// The reducer runs when a dispatch 'upsertMyWorkflowExecutionStatus' was triggered
	// Here we need to update an existing workflow in the store
	on(upsertMyWorkflowExecutionStatus,
		(state, { taskId, executionStatus }) => {
			return { ...state, value: updateExecutionStatus(state.value, taskId, executionStatus) };

		}
	),

	on(updateMyRequestStageApprove,
		(state, { workflow, stage, approver, nextStage }) => {
			return { ...state, value: updateRequestStageApproveAction(state.value, workflow, stage, approver, nextStage) };
		}
	),
);


function updateExecutionStatus(currentWorkflows: IWorkflowRequest[], taskId: string, executionStatus: WorkflowExecutionStatus): IWorkflowRequest[] {
	const workflowToUpdateIndex = currentWorkflows.findIndex(x => x.taskId === taskId);
	if (workflowToUpdateIndex > -1){
		currentWorkflows[workflowToUpdateIndex].executionStatus = executionStatus;
	}
	return currentWorkflows;
}

function updateWorkflowByReference(currentWorkflows: IWorkflowRequest[], updatedWorkflow: IWorkflowRequest): IWorkflowRequest[] {
	const workflowToUpdateIndex = currentWorkflows.findIndex(x => x.taskId === updatedWorkflow.taskId);
	if (workflowToUpdateIndex !== -1)
	{
		currentWorkflows[workflowToUpdateIndex] = parseFrontEndObjectProperties(updatedWorkflow)
	}
	else
	{
		currentWorkflows.push(parseFrontEndObjectProperties(updatedWorkflow));
	}

	return currentWorkflows;
}

function updateRequestStageApproveAction(currentWorkflows: IWorkflowRequest[], updatedWorkflow: IWorkflowRequest, stage: IWorkflowRequestApprovalStage, approver: WorkflowRequestApprover, nextStage?: IWorkflowRequestApprovalStage): IWorkflowRequest[] {
	const workflowToUpdateIndex = currentWorkflows.findIndex(x => x.taskId === updatedWorkflow.taskId);
	const workflowStageToUpdateIndex = currentWorkflows[workflowToUpdateIndex].stages.findIndex(x => x.order === stage.order);
	const workflowStageApproverToUpdateIndex = currentWorkflows[workflowToUpdateIndex].stages[workflowStageToUpdateIndex].approvers.findIndex(x => x.id === approver.id);

	currentWorkflows[workflowToUpdateIndex].stages[workflowStageToUpdateIndex].status = approver.approvalStatus;
	currentWorkflows[workflowToUpdateIndex].stages[workflowStageToUpdateIndex].approvers[workflowStageApproverToUpdateIndex] = approver;

	if (nextStage) {
		currentWorkflows[workflowToUpdateIndex].stages[workflowStageToUpdateIndex + 1] = nextStage;
	}

	currentWorkflows[workflowToUpdateIndex] = parseFrontEndObjectProperties(currentWorkflows[workflowToUpdateIndex]);

	if (approver.approvalStatus === WorkflowRequestStatus.Rejected) {
		currentWorkflows[workflowToUpdateIndex].stages.forEach(stageIterator => {
			if (stageIterator.status === WorkflowRequestStatus.Pending) { stageIterator.status = WorkflowRequestStatus.Cancelled; }
		})
	}


	if (!currentWorkflows[workflowToUpdateIndex].stages.find(x => x.status === WorkflowRequestStatus.Pending)) {
		currentWorkflows[workflowToUpdateIndex].status = currentWorkflows[workflowToUpdateIndex].stages.find(x => x.status === WorkflowRequestStatus.Rejected) ? WorkflowRequestStatus.Rejected : WorkflowRequestStatus.Completed;
	}

	return currentWorkflows;
}